import { Injectable } from "@angular/core";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

import { PublishedPricelist } from "../../../shared/types/pricelist.types";
import { formatLeadTimeWeeksLabel } from "../../../shared/utils/lead-time-weeks.util";
import { ExportDownloadService } from "../export-download/export-download.service";

export interface PreorderBrandReportRow {
    name: string;
    mov: string;
    eta: string;
    downloadHref: string;
    viewHref: string;
    offerId: string | null;
}

const PDF_MARGIN_MM = 6;
const PDF_PAGE_WIDTH_MM = 210;
const PDF_PAGE_HEIGHT_MM = 297;
const RENDER_WIDTH_PX = 794;

@Injectable({
    providedIn: "root",
})
export class PreorderBrandReportService {
    constructor(private exportDownloadService: ExportDownloadService) {}

    async downloadPdfReport(pricelists: PublishedPricelist[]): Promise<void> {
        const brands = this.buildBrandRows(pricelists);
        const host = document.createElement("div");
        host.setAttribute("data-preorder-brand-report", "true");
        host.style.cssText = [
            "position:fixed",
            "left:-10000px",
            "top:0",
            `width:${RENDER_WIDTH_PX}px`,
            "background:#fff",
            "z-index:-1",
            "pointer-events:none",
        ].join(";");
        host.innerHTML = this.buildReportBodyHtml(brands);
        document.body.appendChild(host);

        try {
            await this.waitForImages(host);
            const canvas = await html2canvas(host, {
                backgroundColor: "#ffffff",
                scale: 2,
                useCORS: true,
                logging: false,
                width: RENDER_WIDTH_PX,
                windowWidth: RENDER_WIDTH_PX,
            });

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
                compress: true,
            });

            const hostBounds = host.getBoundingClientRect();
            const contentWidthMm = PDF_PAGE_WIDTH_MM - PDF_MARGIN_MM * 2;
            const mmPerCssPixel = contentWidthMm / hostBounds.width;
            const links = Array.from(host.querySelectorAll<HTMLAnchorElement>("a[href]"))
                .map(anchor => {
                    const bounds = anchor.getBoundingClientRect();
                    return {
                        url: anchor.href,
                        x: (bounds.left - hostBounds.left) * mmPerCssPixel,
                        y: (bounds.top - hostBounds.top) * mmPerCssPixel,
                        width: bounds.width * mmPerCssPixel,
                        height: bounds.height * mmPerCssPixel,
                    };
                });
            const contentHeightMm = PDF_PAGE_HEIGHT_MM - PDF_MARGIN_MM * 2;
            const imgHeightMm = (canvas.height * contentWidthMm) / canvas.width;
            const pageCanvasHeightPx =
                (contentHeightMm / imgHeightMm) * canvas.height;

            let renderedHeightPx = 0;
            let pageIndex = 0;

            while (renderedHeightPx < canvas.height) {
                if (pageIndex > 0) {
                    pdf.addPage();
                }

                const sliceHeightPx = Math.min(
                    pageCanvasHeightPx,
                    canvas.height - renderedHeightPx,
                );
                const sliceCanvas = document.createElement("canvas");
                sliceCanvas.width = canvas.width;
                sliceCanvas.height = Math.max(1, Math.ceil(sliceHeightPx));
                const ctx = sliceCanvas.getContext("2d");
                if (!ctx) {
                    throw new Error("Unable to render PDF page");
                }
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
                ctx.drawImage(
                    canvas,
                    0,
                    renderedHeightPx,
                    canvas.width,
                    sliceHeightPx,
                    0,
                    0,
                    canvas.width,
                    sliceHeightPx,
                );

                const sliceHeightMm =
                    (sliceHeightPx * contentWidthMm) / canvas.width;
                pdf.addImage(
                    sliceCanvas.toDataURL("image/jpeg", 0.95),
                    "JPEG",
                    PDF_MARGIN_MM,
                    PDF_MARGIN_MM,
                    contentWidthMm,
                    sliceHeightMm,
                    undefined,
                    "FAST",
                );

                // The report is rasterized, so preserve its links as PDF annotations.
                const pageTopMm = renderedHeightPx * contentWidthMm / canvas.width;
                for (const link of links) {
                    const top = Math.max(link.y, pageTopMm);
                    const bottom = Math.min(link.y + link.height, pageTopMm + sliceHeightMm);
                    if (bottom > top && link.width > 0) {
                        pdf.link(PDF_MARGIN_MM + link.x, PDF_MARGIN_MM + top - pageTopMm,
                            link.width, bottom - top, { url: link.url });
                    }
                }

                renderedHeightPx += sliceHeightPx;
                pageIndex += 1;
            }

            const date = new Date().toISOString().slice(0, 10);
            pdf.save(`venduro-pre-order-brand-portfolio-${date}.pdf`);
        } finally {
            host.remove();
        }
    }

    buildBrandRows(pricelists: PublishedPricelist[]): PreorderBrandReportRow[] {
        const byBrand = new Map<string, PublishedPricelist[]>();

        for (const pricelist of pricelists) {
            const names = (pricelist.brands || "")
                .split(",")
                .map((name) => name.trim())
                .filter(Boolean);
            if (names.length === 0) {
                continue;
            }
            for (const name of names) {
                const offers = byBrand.get(name) ?? [];
                offers.push(pricelist);
                byBrand.set(name, offers);
            }
        }

        const origin = window.location.origin.replace(/\/$/, "");
        const rows: PreorderBrandReportRow[] = [];

        for (const [name, offers] of byBrand.entries()) {
            const best = this.pickBestOffer(offers);
            rows.push({
                name,
                mov: this.formatMov(best?.mov ?? null),
                eta: this.formatEta(best),
                downloadHref: best
                    ? this.buildOfferDownloadHref(best.id)
                    : `${origin}/pricelists/pre-order`,
                viewHref: best
                    ? `${origin}/category/products/${encodeURIComponent(String(best.id))}`
                    : `${origin}/pricelists/pre-order`,
                offerId: best?.id ?? null,
            });
        }

        return rows.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
        );
    }

    private pickBestOffer(
        offers: PublishedPricelist[],
    ): PublishedPricelist | null {
        if (offers.length === 0) {
            return null;
        }
        return [...offers].sort((a, b) => {
            const movA = a.mov ?? Number.POSITIVE_INFINITY;
            const movB = b.mov ?? Number.POSITIVE_INFINITY;
            if (movA !== movB) {
                return movA - movB;
            }
            return String(a.id).localeCompare(String(b.id));
        })[0];
    }

    private buildOfferDownloadHref(offerId: string): string {
        try {
            return this.exportDownloadService.buildShareableUrl(
                `/products/csv/supplier/${encodeURIComponent(String(offerId))}`,
                "xlsx",
            );
        } catch {
            return `${window.location.origin.replace(/\/$/, "")}/pricelists/pre-order`;
        }
    }

    private formatMov(mov: number | null | undefined): string {
        if (mov == null || !Number.isFinite(mov)) {
            return "—";
        }
        return `€${(mov / 100).toLocaleString("en-US", {
            maximumFractionDigits: 0,
        })}`;
    }

    private formatEta(offer: PublishedPricelist | null): string {
        if (!offer) {
            return "—";
        }
        if (offer.preOrderShippingDate?.trim()) {
            return offer.preOrderShippingDate.trim();
        }
        return formatLeadTimeWeeksLabel(offer.leadTimeFrom, offer.leadTimeTo);
    }

    private formatReportDate(date = new Date()): string {
        return date.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
        });
    }

    private escapeHtml(value: string): string {
        return value
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }

    private waitForImages(root: HTMLElement): Promise<void> {
        const images = Array.from(root.querySelectorAll("img"));
        if (images.length === 0) {
            return Promise.resolve();
        }
        return Promise.all(
            images.map(
                (img) =>
                    new Promise<void>((resolve) => {
                        if (img.complete) {
                            resolve();
                            return;
                        }
                        img.onload = () => resolve();
                        img.onerror = () => resolve();
                    }),
            ),
        ).then(() => undefined);
    }

    private buildReportBodyHtml(brands: PreorderBrandReportRow[]): string {
        const reportDate = this.formatReportDate();
        const logoUrl = `${window.location.origin.replace(/\/$/, "")}/assets/icons/venduro-brand-report-logo.png`;
        const rowsHtml = brands
            .map(
                (brand) => `
          <tr style="border-bottom:1px solid #eceef2">
            <td style="padding:6px 10px;font-weight:700;color:#0d182d">${this.escapeHtml(brand.name)}</td>
            <td style="padding:6px 10px;font-variant-numeric:tabular-nums">${this.escapeHtml(brand.mov)}</td>
            <td style="padding:6px 10px">${this.escapeHtml(brand.eta)}</td>
            <td style="padding:6px 10px;text-align:right;white-space:nowrap">
              <a href="${this.escapeHtml(brand.downloadHref)}" style="text-decoration:none;display:inline-flex;align-items:center;height:20px;padding:0 9px;background:#eef1f6;color:#1f2a44;border-radius:5px;font-size:10.5px;font-weight:600;margin-left:6px">Download</a>
              <a href="${this.escapeHtml(brand.viewHref)}" style="text-decoration:none;display:inline-flex;align-items:center;height:20px;padding:0 9px;background:#0d182d;color:#fff;border-radius:5px;font-size:10.5px;font-weight:600;margin-left:6px">View ↗</a>
            </td>
          </tr>`,
            )
            .join("");

        return `
  <div style="font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,sans-serif;color:#1f2a44;width:100%;background:#fff;padding:0;margin:0">
    <div style="display:flex;align-items:center;justify-content:space-between;padding-bottom:14px;margin-bottom:18px;border-bottom:2px solid #0d182d">
      <img src="${this.escapeHtml(logoUrl)}" alt="Venduro" crossorigin="anonymous" style="height:20px;display:block">
      <div style="font-size:10.5px;color:#8a93a3">Pre-Order Brand Portfolio · ${this.escapeHtml(reportDate)}</div>
    </div>

    <div style="font-size:26px;font-weight:800;font-family:Manrope,Inter,sans-serif;color:#0d182d;letter-spacing:-0.01em">Pre-Order Brand Portfolio</div>
    <div style="font-size:13px;color:#5b6472;margin-top:6px">${this.escapeHtml(reportDate)}</div>
    <div style="font-size:12.5px;color:#3c4454;line-height:1.5;margin-top:8px">Below are the best available pre-order offers across our brand portfolio. Minimum order values (MOV) and estimated arrival (ETA) reflect current supplier terms and may change once confirmed.</div>

    <table style="margin-top:18px;font-size:11.5px;border-collapse:collapse;width:100%">
      <thead>
        <tr style="background:#f7f8fa;border-bottom:1px solid #e6e9ef">
          <th style="text-align:left;padding:7px 10px;font-size:10px;font-weight:700;color:#8a93a3;text-transform:uppercase;letter-spacing:.03em">Brand</th>
          <th style="text-align:left;padding:7px 10px;font-size:10px;font-weight:700;color:#8a93a3;text-transform:uppercase;letter-spacing:.03em">MOV</th>
          <th style="text-align:left;padding:7px 10px;font-size:10px;font-weight:700;color:#8a93a3;text-transform:uppercase;letter-spacing:.03em">ETA</th>
          <th style="text-align:right;padding:7px 10px;font-size:10px;font-weight:700;color:#8a93a3;text-transform:uppercase;letter-spacing:.03em">Links</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml || `<tr><td colspan="4" style="padding:16px 10px;color:#8a93a3">No pre-order brands available.</td></tr>`}
      </tbody>
    </table>

    <div style="margin-top:26px;padding-top:20px;border-top:1px solid #e6e9ef">
      <div style="font-size:14px;font-weight:700;color:#0d182d">How to order</div>
      <div style="font-size:12.5px;color:#3c4454;line-height:1.7;margin-top:6px">
        Place your order directly at <span style="font-weight:600;color:#2E62D9">venduro.io</span>, or email your selection to <span style="font-weight:600;color:#2E62D9">orders@venduro.io</span> and our team will confirm availability and issue a proforma invoice.
      </div>
    </div>

    <div style="margin-top:18px">
      <div style="font-size:14px;font-weight:700;color:#0d182d">Looking for a brand not listed here?</div>
      <div style="font-size:12.5px;color:#3c4454;line-height:1.7;margin-top:6px">
        Contact <span style="font-weight:600;color:#2E62D9">sales@venduro.io</span> or +37067129200 (available on WhatsApp) and we'll source an offer for you.
      </div>
    </div>

    <div style="display:flex;align-items:center;justify-content:space-between;margin-top:24px;padding-top:10px;border-top:1px solid #e6e9ef;font-size:10px;color:#98a1b2">
      <div>Venduro B2B Wholesale · venduro.io</div>
      <div>orders@venduro.io</div>
    </div>
  </div>`;
    }
}
